import React, { useState } from 'react';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const TeamAccessStep = ({ data, departmentInfo, employeeData, onComplete, onBack, onCancel, loading }) => {
  const [teamMembers, setTeamMembers] = useState(data || []);
  const [currentMember, setCurrentMember] = useState({
    email: '',
    role: '',
    permissions: []
  });
  const [errors, setErrors] = useState({});

  const roleTemplates = [
    {
      value: 'department_head',
      label: 'Department Head',
      description: 'Full access to all department functions',
      permissions: ['view_all', 'edit_all', 'approve_all', 'manage_users', 'view_reports', 'export_data']
    },
    {
      value: 'hr_coordinator',
      label: 'HR Coordinator',
      description: 'Manage employees, leave, and compliance',
      permissions: ['view_employees', 'edit_employees', 'manage_leave', 'view_compliance', 'view_reports']
    },
    {
      value: 'payroll_officer',
      label: 'Payroll Officer',
      description: 'Process payroll and manage payments',
      permissions: ['view_payroll', 'process_payroll', 'view_employees', 'view_compliance', 'export_payroll']
    },
    {
      value: 'compliance_officer',
      label: 'Compliance Officer',
      description: 'Monitor and manage compliance requirements',
      permissions: ['view_compliance', 'manage_compliance', 'view_reports', 'export_data']
    },
    {
      value: 'admin_staff',
      label: 'Administrative Staff',
      description: 'Basic access for administrative tasks',
      permissions: ['view_employees', 'view_leave', 'view_reports']
    },
    {
      value: 'viewer',
      label: 'Viewer',
      description: 'Read-only access to department data',
      permissions: ['view_employees', 'view_reports']
    }
  ];

  const handleAddMember = () => {
    const newErrors = {};

    if (!currentMember?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(currentMember?.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!currentMember?.role) {
      newErrors.role = 'Role is required';
    }

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    const selectedRole = roleTemplates?.find(r => r?.value === currentMember?.role);
    
    setTeamMembers(prev => [
      ...prev,
      {
        id: Date.now(),
        email: currentMember?.email,
        role: currentMember?.role,
        roleName: selectedRole?.label,
        permissions: selectedRole?.permissions || []
      }
    ]);

    setCurrentMember({ email: '', role: '', permissions: [] });
    setErrors({});
  };

  const handleRemoveMember = (id) => {
    setTeamMembers(prev => prev?.filter(m => m?.id !== id));
  };

  const handleComplete = () => {
    onComplete?.(teamMembers);
  };

  const handleSkip = () => {
    onComplete?.([]);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Team Access Setup</h2>
        <p className="text-gray-600">
          Assign roles and permissions to team members for {departmentInfo?.departmentName || 'your department'}.
        </p>
      </div>

      {/* Role Templates Info */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Icon name="Info" size={20} className="text-blue-600 mr-3 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-1">Role-Based Access Control</p>
            <p className="text-xs text-gray-600">
              Select from pre-configured role templates that align with PNG government department structures.
              You can modify permissions later in Team Access Management.
            </p>
          </div>
        </div>
      </div>

      {/* Add Team Member Form */}
      <div className="mb-6 border rounded-lg p-6 bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Team Member</h3>
        
        <div className="space-y-4">
          <Input
            label="Email Address"
            type="email"
            value={currentMember?.email}
            onChange={(e) => {
              setCurrentMember(prev => ({ ...prev, email: e?.target?.value }));
              if (errors?.email) setErrors(prev => ({ ...prev, email: '' }));
            }}
            error={errors?.email}
            placeholder="user@department.gov.pg"
          />

          <Select
            label="Role"
            options={roleTemplates}
            value={currentMember?.role}
            onChange={(value) => {
              setCurrentMember(prev => ({ ...prev, role: value }));
              if (errors?.role) setErrors(prev => ({ ...prev, role: '' }));
            }}
            error={errors?.role}
            placeholder="Select role"
          />

          {currentMember?.role && (
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <p className="text-sm font-semibold text-gray-900 mb-2">Permissions:</p>
              <div className="flex flex-wrap gap-2">
                {roleTemplates
                  ?.find(r => r?.value === currentMember?.role)
                  ?.permissions?.map((permission) => (
                    <span
                      key={permission}
                      className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium"
                    >
                      {permission?.replace(/_/g, ' ')}
                    </span>
                  ))}
              </div>
            </div>
          )}

          <Button
            onClick={handleAddMember}
            iconName="UserPlus"
            fullWidth
          >
            Add Team Member
          </Button>
        </div>
      </div>

      {/* Team Members List */}
      {teamMembers?.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Team Members ({teamMembers?.length})
          </h3>
          <div className="space-y-3">
            {teamMembers?.map((member) => (
              <div
                key={member?.id}
                className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg"
              >
                <div className="flex items-center flex-1">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mr-3">
                    <Icon name="User" size={20} className="text-emerald-700" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{member?.email}</p>
                    <p className="text-sm text-gray-600">{member?.roleName}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                      {member?.permissions?.length} permissions
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveMember(member?.id)}
                      iconName="Trash2"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {teamMembers?.length === 0 && (
        <div className="mb-6 text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
          <Icon name="Users" size={48} className="text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-2">No team members added yet</p>
          <p className="text-sm text-gray-500">
            Add team members above or skip to configure access later
          </p>
        </div>
      )}

      {/* Summary Info */}
      <div className="mb-6 bg-gradient-to-r from-emerald-50 to-amber-50 rounded-lg p-6 border border-emerald-200">
        <div className="flex items-start">
          <Icon name="CheckCircle" size={24} className="text-emerald-600 mr-3 mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Setup Almost Complete!
            </h3>
            <p className="text-sm text-gray-700 mb-3">
              Review your configuration:
            </p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-center">
                <Icon name="Building2" size={16} className="text-emerald-600 mr-2" />
                Department: {departmentInfo?.departmentName}
              </li>
              <li className="flex items-center">
                <Icon name="Users" size={16} className="text-emerald-600 mr-2" />
                Employees: {employeeData?.length || 0} imported
              </li>
              <li className="flex items-center">
                <Icon name="Key" size={16} className="text-emerald-600 mr-2" />
                Team Access: {teamMembers?.length} members configured
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between pt-6 border-t">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onBack}
            iconName="ArrowLeft"
            disabled={loading}
          >
            Back
          </Button>
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
        <div className="flex gap-2">
          {teamMembers?.length === 0 && (
            <Button
              variant="outline"
              onClick={handleSkip}
              disabled={loading}
            >
              Skip for Now
            </Button>
          )}
          <Button
            onClick={handleComplete}
            loading={loading}
            iconName="CheckCircle"
            iconPosition="right"
          >
            Complete Setup
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TeamAccessStep;